import React, {useState} from "react";
import {Link, useTranslation} from "gatsby-plugin-react-i18next";
import {List, X} from "@phosphor-icons/react";
import {Sheet, SheetContent, SheetTrigger} from "@/components/ui/sheet";
import {Button} from "../ui/button";
import LanguageSwitcher from "./language-switcher";
import {useNavItems} from "./nav-items";
import logoLandscape from "../../images/logo-new/chatlab-landscape-tile.svg";

const MobileLink = ({item, className = "", onClick}) => (
    item.href
        ? <a href={item.href} className={className} onClick={onClick}>{item.label}</a>
        : <Link to={item.to} className={className} onClick={onClick}>{item.label}</Link>
);

const NavbarMobile = () => {
    const {t} = useTranslation();
    const nav = useNavItems();
    const [open, setOpen] = useState(false);
    const close = () => setOpen(false);

    return (
        <header className="sticky top-0 z-30 w-full h-16 bg-[var(--cl-paper)]/90 backdrop-blur-md border-b border-[var(--cl-rule)] nav-mobile">
            <nav className="w-full h-full px-4 flex items-center justify-between gap-3">
                <Link to="/" title={t("ChatLab - Custom ChatGPT for your website")}>
                    <img src={logoLandscape} width={120} height={33} className="w-[120px] relative -top-[2px]" alt="ChatLab"/>
                </Link>
                <div className="flex items-center gap-3">
                    <LanguageSwitcher mobile={true}/>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger aria-label={t("Open menu")} className="p-2 -mr-2 text-[var(--cl-ink)]">
                            {open ? <X size={26}/> : <List size={26}/>}
                        </SheetTrigger>
                        <SheetContent className="w-full sm:max-w-md p-0 flex flex-col bg-[var(--cl-paper)] overflow-y-auto">
                            <div className="px-6 pt-16 pb-6 flex flex-col gap-8">
                                <ul className="flex flex-col">
                                    {nav.primary.map((item) => (
                                        <li key={item.to} className="border-b border-[var(--cl-rule)]">
                                            <Link to={item.to} onClick={close} className="block py-4 text-[22px] font-semibold text-[var(--cl-ink)]">
                                                {item.label}
                                            </Link>
                                            {item.children ? (
                                                <ul className="flex flex-col pb-3 -mt-1">
                                                    {item.children.map((child) => (
                                                        <li key={child.to}>
                                                            <Link to={child.to} onClick={close} className="flex items-baseline gap-2 py-1.5 pl-4 text-[17px] text-[var(--cl-ink-2)]">
                                                                {child.label}
                                                                {child.note ? <span className="text-[12px] text-[var(--cl-muted)]">{child.note}</span> : null}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                                {nav.resources.flatMap((group) => [
                                    {title: group.title, items: group.items},
                                    ...(group.sections || []),
                                ]).map((section) => (
                                    <div key={section.title} className="flex flex-col gap-2">
                                        <span className="cl-eyebrow">{section.title}</span>
                                        <ul className="flex flex-col">
                                            {section.items.map((item) => (
                                                <li key={item.to || item.href}>
                                                    <MobileLink item={item} onClick={close} className="block py-2 text-[17px] text-[var(--cl-ink-2)]"/>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                            <div className="sticky bottom-0 mt-auto px-6 py-5 bg-[var(--cl-paper)] border-t border-[var(--cl-rule)] flex items-center gap-4">
                                <a href={nav.signIn} className="text-[16px] text-[var(--cl-ink-2)] whitespace-nowrap">{t("Sign in")}</a>
                                <Button variant="primary" className="flex-1" asChild>
                                    <a href={nav.signUp}>{t("Sign up")}</a>
                                </Button>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </nav>
        </header>
    );
};

export default NavbarMobile;
